// The words a stored answer reads as — shared by the answers window and Responses › Assessment.
import { formatDateOnly } from '../../../../lib/formatDate';
import { SURVEY_QUESTION_TYPE, type SurveyResponseViewQuestion } from '../surveyTypes';

/** An answer as words. Null when the question was left unanswered. */
export function answerOf(q: SurveyResponseViewQuestion, respondentName: string): string | null {
  if (!q.answered) return null;

  switch (q.type) {
    case SURVEY_QUESTION_TYPE.MultipleChoice:
    case SURVEY_QUESTION_TYPE.Dropdown:
    case SURVEY_QUESTION_TYPE.Checkbox:
      return q.optionTexts.join(', ') || null;
    case SURVEY_QUESTION_TYPE.ShortParagraph:
    case SURVEY_QUESTION_TYPE.LongParagraph:
      return q.answerText ?? null;
    case SURVEY_QUESTION_TYPE.Scale: {
      if (q.number == null) return null;
      // The end label rides on its number, as the answer form showed it.
      const label = q.number === q.scaleMin ? q.scaleMinLabel : q.number === q.scaleMax ? q.scaleMaxLabel : null;
      return label ? `${q.number} — ${label} (of ${q.scaleMin} to ${q.scaleMax})` : `${q.number} (of ${q.scaleMin} to ${q.scaleMax})`;
    }
    case SURVEY_QUESTION_TYPE.Date:
      return q.date ? formatDateOnly(q.date) : null;
    case SURVEY_QUESTION_TYPE.Time:
      return q.time ? q.time.slice(0, 5) : null;
    // The respondent themselves — the name the list already resolved, never one stored here.
    case SURVEY_QUESTION_TYPE.Name:
      return respondentName;
    case SURVEY_QUESTION_TYPE.Division:
      return q.orgUnitName ? (q.sectorName ? `${q.orgUnitName} — ${q.sectorName}` : q.orgUnitName) : null;
    default:
      return null;
  }
}

/** Whether the answer is a machine value — a date, a time, a number — and so set in mono. */
export function isMachineValue(type: SurveyResponseViewQuestion['type']): boolean {
  return type === SURVEY_QUESTION_TYPE.Date || type === SURVEY_QUESTION_TYPE.Time || type === SURVEY_QUESTION_TYPE.Scale;
}
